import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService, NotifyService } from '@app/services';
import { BaseForm } from '@forms/base.form';
import { TimeOffDay } from '@models/time-off-v3/time-off-day.model';
import { WorkSchedule } from '@models/time-off-v3/works-schedule.model';
import { CreateStatHolidayForm, HolidayForm } from '@time-off-v3/forms';
import { TimeOffService } from '@time-off-v3/services';
import { HolidaysTable } from '@time-off-v3/services/holidays-table/holidays-table';
import moment from 'moment';

@Component({
    selector: 'app-holidays-form',
    templateUrl: './holidays.template.html',
    styleUrls: ['./holidays.style.scss'],
})
export class HolidaysForm extends BaseForm implements OnInit {
    @ViewChild('createStatHolidayForm') createStatHolidayForm: CreateStatHolidayForm;
    @ViewChild('holidayModal') holidayModal: DeprecatedModalFormComponent;
    @ViewChild('holidayDeleteModal') holidayDeleteModal: DeprecatedConfirmationDialogComponent;
    @ViewChild('copyCustomHolidaysModal') copyCustomHolidaysModal: DeprecatedConfirmationDialogComponent;
    @Input() workSchedule: WorkSchedule;
    @Input() hasTimeOffImprovement = false;

    isStatHolidayModalOpen = false;
    holidaysTable: HolidaysTable;
    headerText: string;
    menuSelectedItem: string;
    countryCode = {
        Canada: 'ca',
        'United States': 'us',
    };

    constructor(
        private timeOffService: TimeOffService,
        private notify: NotifyService,
        private analyticsService: AnalyticService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.holidaysTable = new HolidaysTable(this.workSchedule);
    }

    get year(): number {
        return this.holidaysTable.activeYear || new Date().getFullYear();
    }

    showCreateStatHolidayModel(): void {
        this.isStatHolidayModalOpen = true;
    }

    hideCreateStatHolidayModel(): void {
        this.isStatHolidayModalOpen = false;
    }

    showCreateCustomHolidayModal(holidayForm: HolidayForm): void {
        holidayForm.holiday = new TimeOffDay();
        holidayForm.goToDateInView(moment(`${this.year}-01-01`));
        this.headerText = 'time-off-v3.addCustomHoliday';
        this.holidayModal.show();
    }

    async prefillHolidays(): Promise<void> {
        try {
            await this.timeOffService.prefillHolidays(
                this.workSchedule,
                this.createStatHolidayForm.province,
                this.countryCode[this.createStatHolidayForm.country] ?? 'ca',
                this.holidaysTable.activeYear
            );
            await this.holidaysTable.reload();

            this.analyticsService.trackEvent(AnalyticEvents.TimeOffEditWorkScheduleAddStatHolidaysSave);

            this.hideCreateStatHolidayModel();
        } catch (err) {
            this.notify.error(err);
        }
    }

    async onHolidaySave(form: HolidayForm): Promise<void> {
        try {
            form.holiday.calendarDayTypeId = 2;
            form.holiday.workScheduleId = this.workSchedule.id;
            await form.holiday.save();
            this.holidaysTable.goToYear(moment(form.holiday.date).year());
            this.menuSelectedItem === 'edit'
                ? this.analyticsService.trackEvent(AnalyticEvents.TimeOffEditWorkScheduleAddCustomHolidaysSave)
                : this.analyticsService.trackEvent(AnalyticEvents.TimeOffEditHolidaySave);
        } catch (err) {
            this.notify.error(err);
        }
    }

    async menuAction(event: MenuColumnEvent, holidayForm: HolidayForm): Promise<void> {
        const { menuItem, data } = event;
        const timeOffDay = data as TimeOffDay;

        if (menuItem.event === 'edit') {
            this.menuSelectedItem = menuItem.event;

            this.handleUpdateMenuAction(holidayForm, timeOffDay);
            return;
        }

        await this.handleDeleteMenuAction(timeOffDay);
    }

    async onCopyCustomHolidays(year: number): Promise<void> {
        const approved = await this.copyCustomHolidaysModal.show();

        if (!approved) {
            return;
        }

        await this.timeOffService.copyCustomHolidays(this.workSchedule, {
            fromYear: year - 1,
            toYear: year,
        });

        this.holidaysTable.reload();
    }

    private handleUpdateMenuAction(holidayForm: HolidayForm, timeOffDay: TimeOffDay): void {
        holidayForm.holiday = timeOffDay;
        holidayForm.goToDateInView(moment(timeOffDay.date));
        this.headerText = 'time-off-v3.editHoliday';
        this.holidayModal.show();
    }

    private async handleDeleteMenuAction(timeOffDay: TimeOffDay): Promise<void> {
        const approved = await this.holidayDeleteModal.show();

        if (!approved) {
            return;
        }

        try {
            await timeOffDay.delete();
            this.analyticsService.trackEvent(AnalyticEvents.TimeOffEditWorkScheduleDeleteHolidaysSave);
        } catch (err) {
            this.notify.error(err);
        }
        this.holidaysTable.reload();
        this.notify.success('time-off-v3.holidayDeletedSuccessfully');
    }
}
